// Port of scripts/categorize.py (rule-based path only).
//
// The desktop pipeline has three categorization tiers: (1) regex rules
// against vendor_key, (2) batched LLM call when ANTHROPIC_API_KEY is set,
// (3) "Uncategorized / Needs Review" + queued for manual review. The web
// app is strictly tier-1 (no LLM, ever — the whole pitch is "your data
// never leaves your machine"). The cache step is preserved: an existing
// category_cache.json in the user's data folder takes precedence over
// rules, so prior manual recategorizations propagate.

import type { Transaction } from "./common.js";

export interface CategoryAssignment {
  category: string;
  subcategory: string;
}

// Order matters — first match wins. Mirrors the RULES list in
// scripts/categorize.py. Patterns operate on the lowercase vendor_key
// (already normalized) so we don't need /i flags.
const RULES: Array<[RegExp, string, string]> = [
  // ── Personal / household rules ─────────────────────────────────
  [/\b(coffee|cafe|caffe|espresso)\b/, "Restaurants", "Coffee Shop"],
  [/\b(pizza|burger|kfc|popeyes|taco bell|chipotle|wendy|harvey)\b/, "Restaurants", "Fast Food"],
  [/\b(restaurant|grill|kitchen|bistro|diner|bbq|kabob|kebab|biryani|shawarma|sushi|thai|chinese|indian)\b/, "Restaurants", "Dine-in"],
  [/\b(grocer|supermarket|market|fresh|farms|halal meat|butcher|food basics)\b/, "Groceries", "Supermarket"],
  [/\b(pharmacy|drug mart|rexall|guardian)\b/, "Health", "Pharmacy"],
  [/\b(petro|shell|esso|husky|ultramar|gas|fuel|chevron|mobil)\b/, "Travel", "Gas/Fuel"],
  [/\b(rogers|bell|telus|fido|koodo|chatr|freedom mobile|virgin mobile)\b/, "Household", "Internet/Phone"],
  [/\b(hydro|enbridge|gas company|water|utility|union gas)\b/, "Household", "Utilities"],
  [/\b(monthly plan fee|service charge|nsf|overdraft|interest charge|annual fee)\b/, "Financial", "Bank Fees"],
  [/\b(payroll|direct deposit|salary|gov|cra refund|cesb|gst|hst|cra)\b/, "Income", "Salary"],
  [/\b(presto|ttc|go transit|via rail|metrolinx)\b/, "Travel", "Public Transit"],
  [/\b(netflix|spotify|disney|crave|apple\.com|google one|icloud|youtube premium|prime video)\b/, "Subscriptions", "Software"],
  [/\b(zakat|sadaqah|islamic relief|annur|masjid|mosque|charity|foundation)\b/, "Charity", "Donations"],

  // ── Business / corporate rules (QBO Bank Rules + Wave defaults) ─
  // Cloud hosting / infra — catches dynamic names like "AWS *us-east-1".
  [/\b(aws|amazon web services|google cloud|gcp|azure|digitalocean|linode|vercel|heroku|netlify|cloudflare|render\.com|fastly|akamai)\b/, "Software & Tools", "Cloud Hosting"],
  // Dev tools / source control / AI APIs
  [/\b(github|gitlab|bitbucket|jetbrains|npm inc|npmjs|anthropic|openai|replicate|huggingface)\b/, "Software & Tools", "Development Tools"],
  // Communications SaaS
  [/\b(zoom\.us|slack|twilio|sendgrid|mailgun|postmark|intercom)\b/, "Software & Tools", "Communications"],
  // Productivity / workspace
  [/\b(notion|linear app|asana|trello|monday\.com|atlassian|jira|confluence|google workspace|g\s*suite|microsoft 365|office 365|dropbox|box\.com)\b/, "Software & Tools", "Productivity"],
  // Design SaaS
  [/\b(figma|adobe|canva|sketch app|invision)\b/, "Software & Tools", "Design"],
  // CRM / customer success
  [/\b(hubspot|salesforce|pipedrive|zendesk|freshdesk|helpscout)\b/, "Software & Tools", "CRM"],
  // Accounting SaaS
  [/\b(quickbooks|intuit|xero|freshbooks|waveapps|wave financial)\b/, "Software & Tools", "Accounting Software"],
  // Domain / DNS
  [/\b(godaddy|namecheap|cloudflare registrar|hover\.com|gandi)\b/, "Software & Tools", "Domain/DNS"],

  // Payment processing
  [/\b(stripe(?:\.com)?|stripe payments)\b/, "Bank & Payment Processing", "Stripe Fees"],
  [/\b(square inc|squareup|square up)\b/, "Bank & Payment Processing", "Square Fees"],
  [/\b(moneris|global payments|chase merchant|tsys)\b/, "Bank & Payment Processing", "Other Processing"],
  [/\b(wire fee|wire transfer fee|swift fee)\b/, "Bank & Payment Processing", "Wire Fees"],

  // Payroll services
  [/\b(gusto|rippling|justworks|adp|paychex|wagepoint|paycom|paylocity)\b/, "Payroll & Contractors", "Payroll Service Fees"],
  [/\b(deel|remote\.com|oyster hr|papaya global)\b/, "Payroll & Contractors", "Contractor Payments"],

  // Advertising / marketing platforms
  [/\b(google ads|google adwords)\b/, "Advertising & Marketing", "Online Ads"],
  [/\b(facebook ads|meta platforms|meta ads|instagram ads|linkedin ads|tiktok ads|twitter ads|x ads|x corp)\b/, "Advertising & Marketing", "Social Media Ads"],
  [/\b(mailchimp|constant contact|klaviyo|convertkit|active campaign)\b/, "Advertising & Marketing", "Email Marketing"],

  // Shipping / logistics
  [/\b(fedex)\b/, "Shipping & Postage", "FedEx"],
  [/\b(\bups\b(?!\s*cap)|united parcel service)\b/, "Shipping & Postage", "UPS"],
  [/\b(usps|us postal|canada post)\b/, "Shipping & Postage", "USPS/Canada Post"],
  [/\b(dhl)\b/, "Shipping & Postage", "DHL"],
  [/\b(shipstation|stamps\.com|easypost|pirateship)\b/, "Shipping & Postage", "Other Shipping"],

  // Office / facilities
  [/\b(staples|office depot|wb mason|grand & toy)\b/, "Office", "Office Supplies"],
  [/\b(wework|we work|regus|spaces\.com|industrious|knotel)\b/, "Office", "Rent/Lease"],

  // Hiring / HR
  [/\b(indeed\.com|indeed inc|ziprecruiter|linkedin recruiter|glassdoor|workday|bamboohr|gusto hiring)\b/, "Hiring & HR", "Job Boards"],

  // Professional services hints
  [/\b(law firm|llp\b|attorneys?|legal services)\b/, "Professional Services", "Legal"],
  [/\b(cpa\b|chartered (?:professional )?accountant|bookkeeping|bookkeeper)\b/, "Professional Services", "Accounting/Bookkeeping"],

  // ── Added 2026-05-14 desktop sync ────────────────────────────
  // Business banking / fintech
  [/\b(wise business|wise inc|wise payments)\b/, "Bank & Payment Processing", "Currency Conversion"],
  [/\b(mercury\.com|mercury technologies)\b/, "Bank & Payment Processing", "Other Processing"],
  [/\b(brex inc|brex card|brex\.com)\b/, "Bank & Payment Processing", "Other Processing"],
  [/\b(ramp\b|ramp business|ramp\.com)\b/, "Bank & Payment Processing", "Other Processing"],
  [/\b(pleo|relay financial|novo bank|bluevine)\b/, "Bank & Payment Processing", "Other Processing"],

  // E-commerce marketplaces
  [/\b(amzn mktp|amazon seller|amazon mktp|amazon marketplace|amazon fees|amazon payments)\b/, "Marketplace Fees", "Amazon Seller Fees"],
  [/\b(amazon fba|fba fee|fulfillment by amazon)\b/, "Fulfillment & Warehousing", "FBA Fees"],
  [/\b(shopify\b|shopify inc|shopify payments)\b/, "Marketplace Fees", "Shopify Fees"],
  [/\b(etsy inc|etsy fees|etsy\.com)\b/, "Marketplace Fees", "Etsy Fees"],
  [/\b(ebay inc|ebay fees|ebay\.com)\b/, "Marketplace Fees", "eBay Fees"],
  [/\b(walmart marketplace)\b/, "Marketplace Fees", "Walmart Marketplace"],

  // Fulfillment / 3PL
  [/\b(shipbob|shipmonk|deliverr|easyship|red stag)\b/, "Fulfillment & Warehousing", "3PL Services"],

  // More SaaS (analytics, observability, scheduling, no-code)
  [/\b(sentry\.io|sentry inc|datadog|newrelic|new relic|honeycomb\.io|logrocket|logdna|grafana cloud)\b/, "Software & Tools", "Development Tools"],
  [/\b(replicate ai|replicate hq|mistral ai|together ai|cohere|stability ai|perplexity)\b/, "Software & Tools", "Development Tools"],
  [/\b(retool|airtable|webflow|softr|bubble\.io)\b/, "Software & Tools", "Productivity"],
  [/\b(calendly|loom inc|miro labs|figma jam|mural\.co)\b/, "Software & Tools", "Communications"],
  [/\b(typeform|surveymonkey|formstack|jotform)\b/, "Software & Tools", "Productivity"],
  [/\b(clickup|basecamp|height app|fellow app|todoist)\b/, "Software & Tools", "Productivity"],
  [/\b(crisp\.chat|help scout|helpscout|front app|frontapp|intercom|drift\.com)\b/, "Software & Tools", "Communications"],
  [/\b(plausible|fathom analytics|simple analytics|matomo|amplitude inc|mixpanel|posthog)\b/, "Software & Tools", "Productivity"],
  [/\b(beehiiv|substack|convertkit|kit\.com|buttondown)\b/, "Advertising & Marketing", "Email Marketing"],

  // Real estate property management software
  [/\b(appfolio|buildium|yardi|propertyware|cozy\.co|tenantcloud|rentec)\b/, "Software & Tools", "Productivity"],

  // Property maintenance vendor hints
  [/\b(home depot|lowes|rona|canadian tire)\b/, "Property Maintenance", "Plumbing"],
  [/\b(servicemaster|servpro|stanley steemer|molly maid)\b/, "Property Maintenance", "Cleaning (Property)"],

  // Education / training platforms
  [/\b(udemy|coursera|pluralsight|frontend masters|egghead\.io|linkedin learning|skillshare|masterclass)\b/, "Education & Training", "Courses/Tuition"],
  [/\b(o'?reilly media|safari books|manning publications)\b/, "Education & Training", "Books/Learning Subscriptions"],

  // ── Added 2026-05-14 second sync (T&E / hotels / airlines / etc.) ─
  // Hotel chains
  [/\b(marriott|courtyard|residence inn|fairfield|jw marriott|ritz[- ]carlton)\b/, "Business Travel", "Hotel"],
  [/\b(hilton|doubletree|hampton inn|embassy suites|home2 suites|tru by hilton)\b/, "Business Travel", "Hotel"],
  [/\b(hyatt|hyatt place|hyatt house|park hyatt|grand hyatt)\b/, "Business Travel", "Hotel"],
  [/\b(holiday inn|intercontinental|crowne plaza|kimpton|ihg hotels)\b/, "Business Travel", "Hotel"],
  [/\b(wyndham|ramada|days inn|super 8|la quinta)\b/, "Business Travel", "Hotel"],
  [/\b(choice hotels|comfort inn|quality inn|sleep inn|econo lodge)\b/, "Business Travel", "Hotel"],
  [/\b(airbnb|vrbo|homeaway|booking\.com|expedia|hotels\.com|kayak)\b/, "Business Travel", "Hotel"],
  // Airlines
  [/\b(delta air|delta airlines|delta\.com)\b/, "Business Travel", "Flights"],
  [/\b(united airlines|united\.com|ua \d{4})\b/, "Business Travel", "Flights"],
  [/\b(american airlines|aa\.com|americanair)\b/, "Business Travel", "Flights"],
  [/\b(jetblue|jetblue airways)\b/, "Business Travel", "Flights"],
  [/\b(southwest airlines|southwest\.com|swa)\b/, "Business Travel", "Flights"],
  [/\b(alaska airlines|alaska air|alaskaair)\b/, "Business Travel", "Flights"],
  [/\b(air canada|aircanada|westjet|porter airlines)\b/, "Business Travel", "Flights"],
  [/\b(british airways|lufthansa|emirates|qatar airways|singapore airlines|cathay pacific)\b/, "Business Travel", "Flights"],
  // Car rental
  [/\b(hertz)\b/, "Business Travel", "Car Rental"],
  [/\b(avis)\b/, "Business Travel", "Car Rental"],
  [/\b(enterprise rent|enterprise rac|enterprise car)\b/, "Business Travel", "Car Rental"],
  [/\b(budget rent|budget rac|budgetcar)\b/, "Business Travel", "Car Rental"],
  [/\b(national car|national rent|alamo|dollar rent|thrifty rent|sixt)\b/, "Business Travel", "Car Rental"],
  // Tolls
  [/\b(e[- ]?zpass|ez[- ]?pass|nj e-?zpass)\b/, "Tolls & Parking", "Highway Tolls"],
  [/\b(fastrak|fas[- ]?trak)\b/, "Tolls & Parking", "Highway Tolls"],
  [/\b(sunpass|sun[- ]?pass)\b/, "Tolls & Parking", "Highway Tolls"],
  [/\b(407 etr|407etr|highway 407)\b/, "Tolls & Parking", "Highway Tolls"],
  [/\b(toll road|toll bridge|toll plaza)\b/, "Tolls & Parking", "Highway Tolls"],
  [/\b(spothero|parkmobile|parkwhiz|premiere parking|sp\+ parking)\b/, "Tolls & Parking", "Garage/Street Parking"],
  // Cell carriers
  [/\b(verizon wireless|verizon mobile|vzw|vzwrlss)\b/, "Cell Phone & Wireless", "Mobile Plan"],
  [/\b(at&?t mobility|at&?t wireless|att mobility)\b/, "Cell Phone & Wireless", "Mobile Plan"],
  [/\b(t[- ]mobile|tmobile|t-mo\b)\b/, "Cell Phone & Wireless", "Mobile Plan"],
  [/\b(sprint|us cellular|cricket wireless|metropcs|mint mobile|google fi|visible by)\b/, "Cell Phone & Wireless", "Mobile Plan"],
  // Rideshare for business
  [/\b(uber for business|uber business|uberbusiness)\b/, "Business Travel", "Rideshare/Taxi"],
  [/\b(lyft business|lyft for business)\b/, "Business Travel", "Rideshare/Taxi"],
];

export function ruleCategorize(vendorKey: string): CategoryAssignment | null {
  if (!vendorKey) return null;
  for (const [pat, cat, sub] of RULES) {
    if (pat.test(vendorKey)) return { category: cat, subcategory: sub };
  }
  return null;
}

// The shape of category_cache.json (or the in-memory equivalent). Matches
// the desktop side: vendor_key → {category, subcategory}.
export type CategoryCache = Record<string, CategoryAssignment>;

// Apply categorization to a batch of transactions, mutating them in place.
// Lookup order per transaction:
//   1. existing cache (user's prior decisions, including manual recats)
//   2. rule-based regex
//   3. leave as the loader-default ("Uncategorized" / "Needs Review")
//
// Returns the set of newly-learned (vendor_key → assignment) that the
// caller should merge into the cache and persist back to the data folder.
export function categorizeAll(
  transactions: Transaction[],
  cache: CategoryCache = {},
): CategoryCache {
  const learned: CategoryCache = {};
  for (const tx of transactions) {
    const k = tx.vendor_key;
    if (!k) continue;
    if (cache[k]) {
      tx.category = cache[k].category;
      tx.subcategory = cache[k].subcategory;
      continue;
    }
    const ruleHit = ruleCategorize(k);
    if (ruleHit) {
      tx.category = ruleHit.category;
      tx.subcategory = ruleHit.subcategory;
      learned[k] = ruleHit;
    }
    // Otherwise leave loader defaults — the UI surfaces "Needs Review".
  }
  return learned;
}
